import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUS_OPTIONS = ["new", "active", "qualified", "negotiation", "closed_won", "closed_lost"];
const TYPE_NAV = [
  { key: "all", label: "All", hint: "directory" },
  { key: "client", label: "Clients", hint: "studios" },
  { key: "supplier", label: "Suppliers", hint: "vendors" },
  { key: "contractor", label: "Partners", hint: "crew" },
];

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function CRMPage() {
  const [params, setParams] = useSearchParams();
  const reduce = useReducedMotion();
  const typeParam = ["all", "client", "supplier", "contractor"].includes(params.get("type"))
    ? params.get("type")
    : "all";
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setContacts(data || []);
    } catch {
      toast.error("Could not load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(
    () => ({
      all: contacts.length,
      client: contacts.filter((c) => c.type === "client").length,
      supplier: contacts.filter((c) => c.type === "supplier").length,
      contractor: contacts.filter((c) => c.type === "contractor").length,
    }),
    [contacts],
  );

  const filtered = useMemo(() => {
    let list = contacts;
    if (typeParam !== "all") list = list.filter((c) => c.type === typeParam);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        [c.name, c.company, c.email, c.phone, c.notes]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    return [...list].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [contacts, typeParam, search]);

  const setTypeFilter = (type) => {
    const next = new URLSearchParams(params);
    next.set("type", type);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-0 max-w-full overflow-x-clip p-4 sm:p-5 md:p-7" data-testid="crm-page">
      <header className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="label-eyebrow mb-0">Contacts</div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="neo-cta self-start"
          data-testid="add-contact-btn"
        >
          <Plus size={16} strokeWidth={1.4} /> Add contact
        </button>
      </header>

      {loading ? (
        <p className="py-16 text-center text-sm text-[var(--t-meta)]">Loading…</p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="contact-tabs">
            {TYPE_NAV.map((item, idx) => {
              const on = typeParam === item.key;
              const value = counts[item.key] || 0;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTypeFilter(item.key)}
                  className="block text-left transition-transform hover:-translate-y-0.5"
                  data-testid={`tab-${item.key}`}
                >
                  <motion.div
                    initial={reduce ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className={`neo-panel h-full p-4 ${on ? "ring-1 ring-[var(--t-accent)]/30" : ""}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">
                      {item.label}
                    </div>
                    <div className="mt-1 flex items-end justify-between gap-2">
                      <div className="font-heading text-xl tracking-tight !font-light">
                        {value}
                      </div>
                      <span className="mb-1 text-[11px] text-[var(--t-accent)]">{item.hint}</span>
                    </div>
                  </motion.div>
                </button>
              );
            })}
          </div>

          <section className="neo-panel p-5" data-testid="contacts-table">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <div className="label-eyebrow mb-0">Roster</div>
              <span className="text-xs tabular-nums text-[var(--t-meta)]">{filtered.length}</span>
            </div>
            <div className="relative mb-4 w-full">
              <input
                className="t-input w-full !rounded-xl bg-white/50 !pl-11"
                placeholder="Search contacts…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="contact-search"
              />
              <Search
                size={14}
                strokeWidth={1.4}
                className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[var(--t-meta)]"
                aria-hidden
              />
            </div>

            {filtered.length === 0 ? (
              <p className="py-8 text-sm text-[var(--t-meta)]" data-testid="contacts-empty">
                No contacts match.
              </p>
            ) : (
              <div className="divide-y divide-black/5">
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setEditing(c)}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left first:pt-0 last:pb-0"
                    data-testid={`contact-row-${c.id}`}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-heading text-base !font-light hover:text-[var(--t-accent)]">
                        {c.name}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-[var(--t-meta)]">
                        {c.company || "—"}
                      </div>
                    </div>
                    <span className="shrink-0 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]">
                      Edit
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {showAdd ? <ContactModal onClose={() => setShowAdd(false)} onSaved={load} /> : null}
      {editing ? (
        <ContactModal contact={editing} onClose={() => setEditing(null)} onSaved={load} />
      ) : null}
    </div>
  );
}

const emptyForm = () => ({
  name: "",
  company: "",
  type: "client",
  status: "new",
  email: "",
  phone: "",
  notes: "",
});

const contactToForm = (c) => ({
  name: c.name || "",
  company: c.company || "",
  type: c.type || "client",
  status: c.status || "new",
  email: c.email || "",
  phone: c.phone || "",
  notes: c.notes || "",
});

export const ContactModal = ({ contact, onClose, onSaved }) => {
  const isEdit = !!contact;
  const reduce = useReducedMotion();
  const [form, setForm] = useState(() => (contact ? contactToForm(contact) : emptyForm()));
  const [busy, setBusy] = useState(false);

  const persist = async () => {
    if (busy) return false;
    const name = (form.name || "").trim();
    if (!name) return false;
    setBusy(true);
    try {
      const payload = {
        name,
        company: form.company || "",
        type: form.type,
        status: form.status,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || "",
      };
      if (isEdit) {
        await api.patch(`/admin/contacts/${contact.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/contacts", payload);
        toast.success("Contact added");
      }
      onSaved();
      onClose();
      return true;
    } catch {
      toast.error("Could not save");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await persist();
  };

  /** Edit: backdrop / X saves. Create: discard. Cancel always discards. */
  const dismiss = () => {
    if (!isEdit) {
      onClose();
      return;
    }
    if (!(form.name || "").trim()) {
      onClose();
      return;
    }
    void persist();
  };

  const remove = async () => {
    if (!confirm("Delete contact?")) return;
    try {
      await api.delete(`/admin/contacts/${contact.id}`);
      toast.success("Deleted");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/25 p-0 sm:p-4"
      onClick={dismiss}
      data-testid="contact-modal"
    >
      <motion.aside
        initial={reduce ? false : { x: 28, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="neo-panel neo-drawer flex h-full w-full max-w-md flex-col overflow-hidden rounded-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--t-border)] px-6 py-5">
          <div className="min-w-0">
            <div className="label-eyebrow mb-2">{isEdit ? "Edit contact" : "Directory"}</div>
            <div className="font-heading truncate text-2xl tracking-tight !font-extralight">
              {isEdit ? form.name || "Untitled" : "New contact"}
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-xl p-2 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="t-scroll-soft flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="label-eyebrow mb-2 block">Name</label>
              <input
                className="t-input !rounded-xl !bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Company</label>
              <input
                className="t-input !rounded-xl !bg-white"
                value={form.company || ""}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="contact-company-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow mb-2 block">Type</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="client">Client</option>
                    <option value="supplier">Supplier</option>
                    <option value="contractor">Partner</option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Status</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {titleCase(s)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Email</label>
              <input
                type="email"
                className="t-input !rounded-xl !bg-white"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Phone</label>
              <input
                className="t-input !rounded-xl !bg-white"
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Notes</label>
              <textarea
                rows={3}
                className="t-textarea min-h-[96px] !rounded-xl !bg-white"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-[var(--t-border)] px-6 py-4">
            {isEdit ? (
              <button
                type="button"
                onClick={remove}
                className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-error)]"
                data-testid="contact-delete-btn"
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="t-btn-secondary !rounded-xl">
                Cancel
              </button>
              <button
                type="submit"
                className="neo-cta"
                disabled={busy}
                data-testid="contact-submit-btn"
              >
                {busy ? "Saving…" : isEdit ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </form>
      </motion.aside>
    </div>
  );
};
